import type { NavigationBody } from '@ayumia/contracts';
import { text } from '../app/i18n';
import { routeOfTarget, sameRoute, type Route } from '../app/router';

/**
 * Lecture de la navigation pour le cadre (ADR-092) : chemins vers la route courante, badges,
 * initiales. Rien n'autorise ici : le serveur a déjà élagué ce que l'identité ne peut pas ouvrir.
 */

export type NavItem = NavigationBody['items'][number];

/** Route d'une entrée, ou `null` pour un intitulé de section. */
export function routeOfItem(item: NavItem): Route | null {
  return item.target === undefined ? null : routeOfTarget(item.target);
}

/** Route sans l'enregistrement ouvert : un menu désigne une vue ou un objet, pas une fiche. */
export function baseRoute(route: Route): Route {
  if (route.kind === 'view') return { kind: 'view', view_ref: route.view_ref };
  if (route.kind === 'object') return { kind: 'object', object_type: route.object_type };
  if (route.kind === 'task') return { kind: 'inbox' };
  // L'exploration d'un objet et l'objet choisi dans l'atlas restent dans leur entrée de menu (ADR-129, ADR-130).
  if (route.kind === 'graph') return { kind: 'graph' };
  if (route.kind === 'model') return { kind: 'model' };
  return route;
}

/** Chemin des entrées (racine comprise) qui mène à la route, ou `[]`. */
export function trailTo(items: readonly NavItem[], route: Route): NavItem[] {
  const base = baseRoute(route);
  for (const item of items) {
    const own = routeOfItem(item);
    if (own !== null && sameRoute(own, base)) return [item];
    const below = trailTo(item.children ?? [], route);
    if (below.length > 0) return [item, ...below];
  }
  return [];
}

/** Requêtes de badge de l'arbre, sans doublon. */
export function badgeQueries(items: readonly NavItem[]): string[] {
  const found = new Set<string>();
  const walk = (list: readonly NavItem[]): void => {
    for (const item of list) {
      if (item.badge_query !== undefined) found.add(item.badge_query);
      walk(item.children ?? []);
    }
  };
  walk(items);
  return [...found];
}

/** Total des badges d'une entrée et de ses descendants (chaque requête comptée une fois). */
export function badgeTotal(item: NavItem, badges: ReadonlyMap<string, number>): number {
  return badgeQueries([item]).reduce((sum, query) => sum + (badges.get(query) ?? 0), 0);
}

/** Entrées à cible, à plat, avec leur module (premier niveau) : raccourcis de l'accueil. */
export function leaves(items: readonly NavItem[]): Array<{ module: NavItem; item: NavItem }> {
  const out: Array<{ module: NavItem; item: NavItem }> = [];
  const walk = (module: NavItem, list: readonly NavItem[]): void => {
    for (const item of list) {
      if (item.target !== undefined) out.push({ module, item });
      walk(module, item.children ?? []);
    }
  };
  for (const item of items) walk(item, [item]);
  return out;
}

/** Deux initiales au plus, en capitales : « Nemrod Alsace » → « NA ». */
export function initials(label: string | undefined): string {
  const words = (label ?? '').trim().split(/[\s'’.-]+/).filter((word) => word !== '');
  if (words.length === 0) return '?';
  const letters = words.length === 1 ? words[0]!.slice(0, 2) : `${words[0]![0]}${words[words.length - 1]![0]}`;
  return letters.toUpperCase();
}

export function labelOf(item: NavItem): string {
  return text(item.label, 'fr', item.item_id);
}
